<template>
  <form
    class="modal modal--trip-settings"
    @submit="submitForm"
    ref="modalForm"
  >
    <div class="modal__content">
      <div class="modal__header">
        <h2 class="modal__headline">
          {{ $t('misc.buttons.advancedSearchOptions') }}
        </h2>
      </div>

      <div class="modal__body">
        <div class="trip-settings">
<!--          <setting-profiles-->
<!--            :selected-profile="profile"-->
<!--            @profileChanged="setProfile($event, ...arguments)"-->
<!--          />-->

          <ul class="trip-settings__accordions">
            <setting-mot
              :mot="mot"
              @motChanged="setMot($event, ...arguments)"
            />
            <setting-way-to-stop
              :travel-type="travelType"
              :walking-speed="walkingSpeed"
              :path-to-stop="pathToStop"
              @travelTypeChanged="setTravelType($event, ...arguments)"
              @walkingSpeedChanged="setWalkingSpeed($event, ...arguments)"
              @pathToStopChanged="setPathToStop($event, ...arguments)"
            />
<!--            <setting-mobility-restrictions-->
<!--              :wheelchair="wheelchair"-->
<!--              :solid-stairs="solidStairs"-->
<!--              :escalators="escalators"-->
<!--              :least-change="leastChange"-->
<!--              :entrance="entrance"-->
<!--              @wheelchairChanged="setWheelchair($event, ...arguments)"-->
<!--              @solidStairsChanged="setSolidStairs($event, ...arguments)"-->
<!--              @escalatorsChanged="setEscalators($event, ...arguments)"-->
<!--              @leastChangeChanged="setLeastChange($event, ...arguments)"-->
<!--              @entranceChanged="setEntrance($event, ...arguments)"-->
<!--            />-->
            <setting-interchanges
              :solid-stairs="solidStairs"
              :max-changes="maxChanges"
              @maxChangesChanged="setMaxChanges($event, ...arguments)"
              @solidStairsChanged="setSolidStairs($event, ...arguments)"
            />
          </ul>
          <custom-switch-input
            :legend="''"
            :switch-list="[
              {
                id: 'includeAlternativeStops',
                label: $t('tripPlanner.settings.includeAlternativeStops'),
                type: 'checkbox',
                value: true,
                checked: includeAlternativeStops
              }
            ]"
            @set-optionChanged="setIncludeAlternativeStops"
          />
        </div>

        <custom-switch-input
          :legend="''"
          :switch-list="[
            {
              id: 'tripSaveSettings',
              label: $t('tripPlanner.settings.saveSettings'),
              type: 'checkbox',
              value: true,
              checked: saveSettings,
              disabled: saveSettingsDisabled
            }
          ]"
          @set-optionChanged="toggleSaveSettings"
        />
        <basic-control
          :appearance="'link'"
          :icon="'trash'"
          :class="{'--hidden': !anyTemporarySettingDiffersFromDefault}"
          @onClick="resetSettingsToDefault"
        >
          {{ $t('tripPlanner.settings.resetOptionsToDefault') }}
        </basic-control>
      </div>
    </div>

    <div
      class="modal__footer"
      :class="{'modal__footer--sticky': modalFooterSticky}"
      ref="modalFooter"
    >
      <basic-control
        class="modal__footer-actions"
        :decoration="'outlined'"
        :appearance="'button'"
        @onClick="closeModal()"
      >
        {{ $t('misc.buttons.cancel') }}
      </basic-control>
      <basic-control
        class="modal__footer-actions"
        :appearance="'button'"
        :aria-disabled="submitDisabled"
        :attr="{
          'aria-describedby': 'tripSettingsFormSubmitDisabledHint'
        }"
        :type="'submit'"
      >
        {{ $t('misc.buttons.applyAndClose') }}
      </basic-control>
      <span
        v-if="submitDisabled"
        id="tripSettingsFormSubmitDisabledHint"
        class="sr-only"
      >
        {{ $t('tripPlanner.settings.applySettingsHint') }}
      </span>
    </div>
  </form>
</template>

<script>
import { ref } from 'vue'
import { mapActions, mapState } from 'pinia'
import { tripSettingsStore } from '@/stores/tripsettings'
import BasicControl from '@/partials/_buttons/basic-control.vue'
import CustomSwitchInput from '@/partials/_form/switch-input.vue'
import SettingInterchanges from '@/partials/trip-settings/setting-interchanges.vue'
// import SettingMobilityRestrictions from '@/partials/trip-settings/setting-mobility-restrictions.vue'
import SettingMot from '@/partials/trip-settings/setting-mot.vue'
// import SettingProfiles from '@/partials/trip-settings/setting-profiles.vue'
import SettingWayToStop from '@/partials/trip-settings/setting-way-to-stop.vue'
import { arrayContentEqualsIgnoreOrder } from '@/helpers/helpers'
import { useVfm } from 'vue-final-modal'

export default {
  name: 'TripSettings',
  components: {
    BasicControl,
    CustomSwitchInput,
    SettingInterchanges,
    // SettingMobilityRestrictions,
    SettingMot,
    // SettingProfiles,
    SettingWayToStop,
  },
  emits: [
    'confirm',
  ],
  data () {
    return {
      modalFooterSticky: false,
      resizeObserver: null,
      saveSettings: false,
      profile: null,
      mot: [],
      travelType: null,
      bikeOnStop: false,
      walkingSpeed: null,
      pathToStop: null,
      wheelchair: false,
      solidStairs: false,
      escalators: false,
      leastChange: false,
      entrance: null,
      maxChanges: null,
      includeAlternativeStops: true
    }
  },
  watch: {
    includeAlternativeStops (newValue) {
      this.checkIfTemporarySettingDiffersFromDefault({
        settingName: 'includeAlternativeStops',
        settingValue: newValue
      })
    }
  },
  setup () {
    const modalFooter = ref()
    const modalForm = ref()

    const vfm = useVfm()
    const {
      closeAll
    } = vfm

    return {
      modalFooter,
      modalForm,
      vfm,
    }
  },
  created () {
    // standardSettings
    this.mot = this.currentStandardSettings.mot
    this.travelType = this.currentStandardSettings.travelType
    // this.bikeOnStop = this.currentStandardSettings.bikeOnStop
    this.walkingSpeed = this.currentStandardSettings.walkingSpeed
    this.pathToStop = this.currentStandardSettings.pathToStop
    this.maxChanges = this.currentStandardSettings.maxChanges
    this.includeAlternativeStops = this.currentStandardSettings.includeAlternativeStops
    // variousSettings
    this.profile = this.currentVariousSettings.profile
    this.wheelchair = this.currentVariousSettings.wheelchair
    // mobilitySettings
    this.solidStairs = this.currentMobilitySettings.solidStairs
    this.escalators = this.currentMobilitySettings.escalators
    this.leastChange = this.currentMobilitySettings.leastChange
    this.entrance = this.currentMobilitySettings.entrance
  },
  mounted () {
    this.updateModalFooterSticky()
    window.addEventListener('resize:smart', this.updateModalFooterSticky)

    this.resizeObserver = new ResizeObserver(() => {
      this.updateModalFooterSticky()
    })

    this.resizeObserver.observe(this.modalForm);
  },
  beforeUnmount () {
    window.removeEventListener('resize:smart', this.updateModalFooterSticky)
    this.resizeObserver.unobserve(this.modalForm)
  },
  computed: {
    ...mapState(tripSettingsStore, [
      'anySettingsAreSaved',
      'anyTemporarySettingDiffersFromDefault',
      'currentStandardSettings',
      'currentMobilitySettings',
      'currentVariousSettings',
      'defaultStandardSettings',
      'defaultMobilitySettings',
      'defaultVariousSettings',
      'savedStandardSettings',
      'savedMobilitySettings',
      'savedVariousSettings',
    ]),

    /** Disable the submit button in case no setting changed. Enable it in case the user want to save the new settings */
    submitDisabled () {
      return !this.checkIfTemporarySettingsDifferFromCurrentSettings() && !this.saveSettings
    },
    saveSettingsDisabled () {
      if (!this.anySettingsAreSaved) {
        /**
         * no saved settings at all
         * -> every setting could possibly be saved by user
         * -> button not disabled
         */
        return false
      }

      const differs = this.checkIfTemporarySettingsDifferFromSavedSettings()

      /**
       * disable button if no temporary setting differs from saved
       */
      return !differs
    }
  },
  methods: {
    ...mapActions(tripSettingsStore, [
      'modifySettings',
      'updateMobilityRestriction',
      'checkIfAnyCurrentSettingDiffersFromDefault',
      'checkIfTemporarySettingDiffersFromDefault',
      'saveCurrentSettingsThatDifferFromDefault',
    ]),
    setProfile (value) {
      const previouslySelectedProfile = this.profile

      // deactivate old profile
      if (previouslySelectedProfile) {
        this.resetProfile(previouslySelectedProfile)
      }

      // activate new profile
      this.profile = value

      switch (value) {
        case null:
          break
        case 'WHEELCHAIR':
          this.setProfileWheelchair()
          break
        case 'STROLLER':
          this.setProfileStroller()
          break
        case 'BIKE':
          this.setProfileBike()
          break
        case 'SENIOR':
          this.setProfileSenior()
          break
      }
    },
    setProfileWheelchair (activate = true) {
      this.walkingSpeed = activate ? 'SLOW' : this.defaultStandardSettings.walkingSpeed
      this.wheelchair = activate ? true : this.defaultVariousSettings.wheelchair
      this.solidStairs = activate ? false : this.defaultMobilitySettings.solidStairs
      this.escalators = activate ? false : this.defaultMobilitySettings.escalators
      this.entrance = activate ? 'NO_STEP' : this.defaultMobilitySettings.entrance
    },
    setProfileStroller (activate = true) {
      this.solidStairs = activate ? false : this.defaultMobilitySettings.solidStairs
      this.escalators = activate ? false : this.defaultMobilitySettings.escalators
      this.entrance = activate ? 'SMALL_STEP' : this.defaultMobilitySettings.entrance
    },
    setProfileBike (activate = true) {
      this.travelType = activate ? 'BIKE_AND_RIDE' : this.defaultStandardSettings.travelType
      // this.bikeOnStop = activate ? true : this.defaultStandardSettings.bikeOnStop
    },
    setProfileSenior (activate = true) {
      this.walkingSpeed = activate ? 'SLOW' : this.defaultStandardSettings.walkingSpeed
      this.leastChange = activate ? true : this.defaultMobilitySettings.leastChange
      this.entrance = activate ? 'SMALL_STEP' : this.defaultMobilitySettings.entrance
    },
    resetProfile (profile) {
      switch (profile) {
        case null:
          break
        case 'WHEELCHAIR':
          this.setProfileWheelchair(false)
          break
        case 'STROLLER':
          this.setProfileStroller(false)
          break
        case 'BIKE':
          this.setProfileBike(false)
          break
        case 'SENIOR':
          this.setProfileSenior(false)
          break
      }
    },
    setMot (value) {
      this.mot = value
    },
    setTravelType (value) {
      this.travelType = value
    },
    /*
    setBikeOnStop (value) {
      this.bikeOnStop = value
    },
    */
    setWalkingSpeed (value) {
      this.walkingSpeed = value
    },
    setPathToStop (value) {
      this.pathToStop = value
    },
    // setWheelchair (value) {
    //   this.wheelchair = value
    // },
    setSolidStairs (value) {
      this.solidStairs = value
    },
    // setEscalators (value) {
    //   this.escalators = value
    // },
    // setLeastChange (value) {
    //   this.leastChange = value
    // },
    // setEntrance (value) {
    //   this.entrance = value
    // },
    setMaxChanges (value) {
      this.maxChanges = value
    },
    setIncludeAlternativeStops () {
      this.includeAlternativeStops = event.target.checked
    },
    /**
     * Change saveSettings and save settings changed.
     * @function toggleSaveSettings
     */
    toggleSaveSettings () {
      this.saveSettings = !this.saveSettings
    },

    /**
     * Resets all settings to default values.
     * @function resetSettingsToDefault
     */
    resetSettingsToDefault () {
      // 1) profile
      this.profile = null
      // 2) mot
      this.mot = this.defaultStandardSettings.mot
      // 3) waytostop
      this.travelType = this.defaultStandardSettings.travelType
      // this.bikeOnStop = this.defaultStandardSettings.bikeOnStop
      this.walkingSpeed = this.defaultStandardSettings.walkingSpeed
      this.pathToStop = this.defaultStandardSettings.pathToStop
      // 4) mobilityrestrictions
      this.wheelchair = this.defaultVariousSettings.wheelchair
      this.solidStairs = this.defaultMobilitySettings.solidStairs
      this.escalators = this.defaultMobilitySettings.escalators
      this.leastChange = this.defaultMobilitySettings.leastChange
      this.entrance = this.defaultMobilitySettings.entrance
      // 5) interchanges
      this.maxChanges = this.defaultStandardSettings.maxChanges
      // 6) others
      this.includeAlternativeStops = this.defaultStandardSettings.includeAlternativeStops
    },

    updateModalFooterSticky () {
      // footer should not be sticky if it takes more than 1/3 of the wrappers visible height
      let wrapperHeight = this.modalForm ? this.modalForm.offsetHeight : 0
      let footerHeight = this.modalFooter ? this.modalFooter.offsetHeight : 0

      this.modalFooterSticky = footerHeight > (wrapperHeight / 3) ? false : true
    },

    /**
      * Close modal.
      * @function closeModal
      */
    closeModal () {
      this.vfm.closeAll()
    },

    /**
     * Try to submit the form, depending on the disabled state of the submit button.
     * @function submitForm
     */
    submitForm () {
      event.preventDefault()

      if (!this.submitDisabled) {
        this.applySettingsAndCloseModal()
      }
    },

    /**
     * Save new trip settings to store and close modal.
     * @function applySettingsAndCloseModal
     */
    applySettingsAndCloseModal () {
      this.setCurrentSettings()

      if (this.saveSettings) {
        this.saveCurrentSettingsThatDifferFromDefault()
      }

      this.checkIfAnyCurrentSettingDiffersFromDefault()

      this.$emit('confirm')
      this.closeModal()
    },

    /**
     * Updates store (state.current) with the options that should be used for the next trip request (but not after new pageload).
     * @function setCurrentSettings
     */
    setCurrentSettings () {
      this.modifySettings({
        settingsData: {
          mot: this.mot,
          travelType: this.travelType,
          // bikeOnStop: this.bikeOnStop,
          walkingSpeed: this.walkingSpeed,
          pathToStop: this.pathToStop,
          maxChanges: this.maxChanges,
          includeAlternativeStops: this.includeAlternativeStops
        },
        settingsType: 'current',
        settingsGroup: 'standardSettings'
      })

      this.modifySettings({
        settingsData: {
          solidStairs: this.solidStairs,
          escalators: this.escalators,
          leastChange: this.leastChange,
          entrance: this.entrance
        },
        settingsType: 'current',
        settingsGroup: 'mobilitySettings'
      })

      this.modifySettings({
        settingsData: {
          profile: this.profile,
          wheelchair: this.wheelchair
        },
        settingsType: 'current',
        settingsGroup: 'variousSettings'
      })

      this.updateMobilityRestriction({
        wheelchair: this.wheelchair,
        solidStairs: this.solidStairs,
        escalators: this.escalators,
        leastChange: this.leastChange,
        entrance: this.entrance
      })
    },

    checkIfTemporarySettingsDifferFromCurrentSettings () {
      const differs =
        !arrayContentEqualsIgnoreOrder(this.currentStandardSettings.mot, this.mot) ||
        this.travelType !== this.currentStandardSettings.travelType ||
        // this.bikeOnStop !== this.currentStandardSettings.bikeOnStop ||
        this.walkingSpeed !== this.currentStandardSettings.walkingSpeed ||
        this.pathToStop !== this.currentStandardSettings.pathToStop ||
        this.maxChanges !== this.currentStandardSettings.maxChanges ||
        this.includeAlternativeStops !== this.currentStandardSettings.includeAlternativeStops ||
        this.solidStairs !== this.currentMobilitySettings.solidStairs ||
        this.escalators !== this.currentMobilitySettings.escalators ||
        this.leastChange !== this.currentMobilitySettings.leastChange ||
        this.entrance !== this.currentMobilitySettings.entrance ||
        this.wheelchair !== this.currentVariousSettings.wheelchair
        // do not check for profile

      return differs
    },
    /**
     * Check for each single setting.
     * If the setting has NOT been saved, compare the temporary setting with the default setting (because we do not allow to save default settings)
     * If the setting has been saved, compare the temporary setting with the saved setting (because we do not allow to save settings that already have been saved)
     * @function checkIfTemporarySettingsDifferFromSavedSettings
     * @returns {Boolean} differs - any temporary setting differs from saved setting
     */
    checkIfTemporarySettingsDifferFromSavedSettings () {
      // comparing the mot array works little different than the other settings
      let motDiffers = false

      if (this.savedStandardSettings.mot === undefined) {
        if (!arrayContentEqualsIgnoreOrder(this.defaultStandardSettings.mot, this.mot)) {
          motDiffers = true
        }
      } else if (!arrayContentEqualsIgnoreOrder(this.savedStandardSettings.mot, this.mot)) {
        motDiffers = true
      }

      const differs = (
        motDiffers ||
        (this.savedStandardSettings.travelType === undefined ? (this.travelType !== this.defaultStandardSettings.travelType) : (this.travelType !== this.savedStandardSettings.travelType)) ||
        // (this.savedStandardSettings.bikeOnStop === undefined ? (this.bikeOnStop !== this.defaultStandardSettings.bikeOnStop) : (this.bikeOnStop !== this.savedStandardSettings.bikeOnStop)) ||
        (this.savedStandardSettings.walkingSpeed === undefined ? (this.walkingSpeed !== this.defaultStandardSettings.walkingSpeed) : (this.walkingSpeed !== this.savedStandardSettings.walkingSpeed)) ||
        (this.savedStandardSettings.pathToStop === undefined ? (this.pathToStop !== this.defaultStandardSettings.pathToStop) : (this.pathToStop !== this.savedStandardSettings.pathToStop)) ||
        (this.savedStandardSettings.maxChanges === undefined ? (this.maxChanges !== this.defaultStandardSettings.maxChanges) : (this.maxChanges !== this.savedStandardSettings.maxChanges)) ||
        (this.savedStandardSettings.includeAlternativeStops === undefined ? (this.includeAlternativeStops !== this.defaultStandardSettings.includeAlternativeStops) : (this.includeAlternativeStops !== this.savedStandardSettings.includeAlternativeStops)) ||
        (this.savedMobilitySettings.solidStairs === undefined ? (this.solidStairs !== this.defaultMobilitySettings.solidStairs) : (this.solidStairs !== this.savedMobilitySettings.solidStairs)) ||
        (this.savedMobilitySettings.escalators === undefined ? (this.escalators !== this.defaultMobilitySettings.escalators) : (this.escalators !== this.savedMobilitySettings.escalators)) ||
        (this.savedMobilitySettings.leastChange === undefined ? (this.leastChange !== this.defaultMobilitySettings.leastChange) : (this.leastChange !== this.savedMobilitySettings.leastChange)) ||
        (this.savedMobilitySettings.entrance === undefined ? (this.entrance !== this.defaultMobilitySettings.entrance) : (this.entrance !== this.savedMobilitySettings.entrance)) ||
        (this.savedVariousSettings.wheelchair === undefined ? (this.wheelchair !== this.defaultVariousSettings.wheelchair) : (this.wheelchair !== this.savedVariousSettings.wheelchair))
        // do not check for profile
      )

      return differs
    }
  }
}
</script>
